import { useCallback, useEffect, useRef, useState } from "react";
import { useWebSocketContext } from "../contexts/WebSocketProvider";

export default function useAllUsers(initialPage = 1, limit = 20) {
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [page, setPage] = useState(initialPage);
    const [hasMore, setHasMore] = useState(true);
    const { send, isConnected, connect, registerHandler } = useWebSocketContext();
    const mountedRef = useRef(true);
    const retryTimeoutRef = useRef(null);

    const groupId = localStorage.getItem("group_id");
    const roleId = localStorage.getItem("role_id");

    const fetchUsers = useCallback(async (attempt = 0, pageToFetch = page) => {
        if (!mountedRef.current) return;

        setIsLoading(true);
        setError('');
        setMessage('');

        try {
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
                retryTimeoutRef.current = null;
            }

            if (!isConnected) {
                return
            }

            const response = await send('cms_get_all_users', {
                group_id: groupId,
                role_id: roleId,
                page: pageToFetch,
                limit: limit
            });
            console.log('Users response:', response);

            if (!response || response.type !== 'cms_get_all_users_response') {
                throw new Error('Invalid response from server');
            }

            if (!Array.isArray(response.data)) {
                throw new Error('No data available. Try refreshing the page');
            }

            setUsers((prev) => pageToFetch === 1 ? response.data : [...prev, ...response.data]);
            setHasMore(response.data.length === limit); // If less than limit, no more pages
            setMessage(response.message || 'Users fetched successfully');
        } catch (err) {
            setError(err.message);

            if (mountedRef.current) {
                const delay = Math.min(1000 * Math.pow(2, attempt), 30000);
                retryTimeoutRef.current = setTimeout(() => {
                    fetchUsers(attempt + 1, pageToFetch);
                }, delay);
            }
        } finally {
            if (mountedRef.current) setIsLoading(false);
        }
    }, [isConnected, connect, send, groupId, roleId, page, limit]);

    // Live updates (merge incoming users)
    useEffect(() => {
        const unregister = registerHandler('all_users_update', (msg) => {
            if (!mountedRef.current) return;
            if (Array.isArray(msg.data) && msg.data.length > 0) {
                setUsers((prev) => {
                    const updatedMap = new Map();
                    prev.forEach(u => updatedMap.set(u.id, u));
                    msg.data.forEach(u => updatedMap.set(u.id, u));
                    return Array.from(updatedMap.values());
                });
            }
        });

        return () => unregister();
    }, [registerHandler]);

    // Initial fetch
    useEffect(() => {
        mountedRef.current = true;
        fetchUsers(0, page);
        return () => {
            mountedRef.current = false;
            if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
        };
    }, [fetchUsers, page]);

    const loadMore = () => {
        if (hasMore && !isLoading) {
            setPage((prev) => prev + 1);
        }
    };

    return { users, isLoading, error, message, refetch: () => fetchUsers(0, 1), loadMore, hasMore };
}
